import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import mammoth from "mammoth";
import { CircularProgress } from "@mui/material";
import { fontFamily } from "../Constants/constants";

// Component for displaying Excel files as tables
function ExcelViewer({ fileBytes }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      const workbook = XLSX.read(fileBytes, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      setData(jsonData);
      setLoading(false);
    } catch (err) {
      setError("Failed to parse Excel file");
      setLoading(false);
    }
  }, [fileBytes]);

  if (loading)
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
        <CircularProgress />
      </div>
    );
  if (error) return <div style={{ padding: "20px", color: "red", fontFamily }}>{error}</div>;

  return (
    <div style={{ padding: "10px", maxHeight: "600px", overflow: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily, fontSize: "12px" }}>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr key={rowIndex} style={{ borderBottom: "1px solid #ddd" }}>
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  style={{
                    padding: "8px",
                    border: "1px solid #ddd",
                    backgroundColor: rowIndex === 0 ? "#f5f5f5" : "white",
                    fontWeight: rowIndex === 0 ? "bold" : "normal",
                    color: "#000",
                  }}
                >
                  {cell || ""}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Component for displaying Word documents
function WordViewer({ fileBytes }) {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    mammoth
      .convertToHtml({ arrayBuffer: fileBytes })
      .then((result) => {
        setHtml(result.value);
        setLoading(false);
      })
      .catch((err) => {
        setError("Failed to parse Word document");
        setLoading(false);
      });
  }, [fileBytes]);

  if (loading)
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
        <CircularProgress />
      </div>
    );
  if (error) return <div style={{ padding: "20px", color: "red", fontFamily }}>{error}</div>;

  return (
    <div
      style={{
        padding: "20px",
        maxHeight: "600px",
        overflow: "auto",
        fontFamily,
        lineHeight: "1.6",
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// Main Universal Document Viewer Component
function UniversalDocumentViewer({ fileBytes, mimeType, fileName, fileUrl, style }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);

  // Hook for creating and cleaning up blob URLs - moved to top to comply with Rules of Hooks
  useEffect(() => {
    if (fileBytes) {
      if (mimeType === "application/pdf" || fileName?.toLowerCase().endsWith(".pdf")) {
        console.log("📄 Creating PDF blob from file bytes...");
        const blob = new Blob([fileBytes], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        console.log("✅ PDF blob URL created:", url);
        setBlobUrl(url);

        return () => {
          URL.revokeObjectURL(url);
          setBlobUrl(null);
        };
      } else if (mimeType?.startsWith("image/")) {
        console.log("🖼️ Creating image blob from file bytes...");
        const blob = new Blob([fileBytes], { type: mimeType });
        const url = URL.createObjectURL(blob);
        console.log("✅ Image blob URL created:", url);
        setBlobUrl(url);

        return () => {
          URL.revokeObjectURL(url);
          setBlobUrl(null);
        };
      }
    } else {
      console.log("❌ No file bytes provided to UniversalDocumentViewer");
    }
    return () => {}; // Return empty cleanup function if no blob created
  }, [fileBytes, mimeType, fileName]);

  if (fileUrl && !fileBytes) {
    return (
      <embed
        src={`${fileUrl}#navpanes=0&view=FitH`}
        type="application/pdf"
        width="100%"
        height="100%"
        style={{ border: "none", colorScheme: "light", ...style }}
      />
    );
  }

  // If no fileBytes provided, show message
  if (!fileBytes) {
    console.log("❌ UniversalDocumentViewer: No fileBytes provided", {
      fileBytes: fileBytes,
      mimeType: mimeType,
      fileName: fileName,
      fileUrl: fileUrl,
    });
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          fontFamily,
          color: "#666",
        }}
      >
        No file data available
      </div>
    );
  }

  try {
    // For PDFs, use the blob URL created in useEffect
    if (mimeType === "application/pdf" || fileName?.toLowerCase().endsWith(".pdf")) {
      console.log("📄 Rendering PDF with blobUrl:", blobUrl);
      if (blobUrl) {
        return (
          <div
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "#ffffff",
              display: "flex",
              justifyContent: "center",
              overflow: "auto",
              colorScheme: "light",
            }}
          >
            <embed
              src={`${blobUrl}#navpanes=0&view=FitH`}
              type="application/pdf"
              width="100%"
              height="100%"
              style={{ border: "none", display: "block", colorScheme: "light", ...style }}
              onLoad={() => console.log("✅ PDF embed loaded successfully")}
              onError={(e) => console.error("❌ PDF embed load error:", e)}
            />
          </div>
        );
      } else {
        console.log("⏳ Waiting for PDF blob URL to be created...");
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "400px",
              fontFamily,
              color: "#666",
            }}
          >
            Loading PDF...
          </div>
        );
      }
    }

    // For Excel files, parse and display as table
    if (
      mimeType?.includes("spreadsheet") ||
      fileName?.toLowerCase().endsWith(".xlsx") ||
      fileName?.toLowerCase().endsWith(".xls")
    ) {
      return <ExcelViewer fileBytes={fileBytes} />;
    }

    // For Word docs, convert to HTML
    if (
      mimeType?.includes("wordprocessing") ||
      fileName?.toLowerCase().endsWith(".docx") ||
      fileName?.toLowerCase().endsWith(".doc")
    ) {
      return <WordViewer fileBytes={fileBytes} />;
    }

    // For images, use the blob URL created in useEffect
    if (mimeType?.startsWith("image/")) {
      if (blobUrl) {
        return (
          <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
            <img
              src={blobUrl}
              alt={fileName || "Document"}
              style={{ maxWidth: "100%", maxHeight: "600px", objectFit: "contain" }}
            />
          </div>
        );
      } else {
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "400px",
              fontFamily,
              color: "#666",
            }}
          >
            Loading image...
          </div>
        );
      }
    }

    // For text files
    if (mimeType?.startsWith("text/") || fileName?.toLowerCase().endsWith(".txt")) {
      const text = new TextDecoder().decode(fileBytes);
      return (
        <div
          style={{
            padding: "20px",
            maxHeight: "600px",
            overflow: "auto",
            fontFamily: "monospace",
            whiteSpace: "pre-wrap",
            backgroundColor: "#f5f5f5",
          }}
        >
          {text}
        </div>
      );
    }

    console.log("❌ Unsupported file type detected:", {
      mimeType: mimeType,
      fileName: fileName,
      fileExtension: fileName?.split(".").pop()?.toLowerCase(),
      fileBytesLength: fileBytes?.byteLength || 0,
    });
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          fontFamily,
          color: "#666",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        <div>Unsupported file type</div>
        <div style={{ fontSize: "12px" }}>
          File: {fileName || "Unknown"} | Type: {mimeType || "Unknown"}
        </div>
      </div>
    );
  } catch (err) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          fontFamily,
          color: "red",
        }}
      >
        Error loading document: {err.message}
      </div>
    );
  }
}

export default UniversalDocumentViewer;
